/**
 * Live sessions of the Academy (web + mobile): dates in Cameroon time and what the learner can do.
 * The server decides who gets the link and when; these helpers only read its answer.
 */

export const LIVE_TIME_ZONE = "Africa/Douala";

const localeOf = (language: string) => (language.startsWith("en") ? "en-GB" : "fr-FR");

/** ISO date-time → { date: « 26/09/2026 », time: « 10:00 » } in Africa/Douala. */
export function liveWhen(iso: string, language: string): { date: string; time: string } {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return { date: iso, time: "" };
  const locale = localeOf(language);
  return {
    date: value.toLocaleDateString(locale, { timeZone: LIVE_TIME_ZONE }),
    time: value.toLocaleTimeString(locale, { timeZone: LIVE_TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
  };
}

function doualaParts(value: Date): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: LIVE_TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(value);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
}

/** « 2026-09-26T10:00 » typed in Cameroon time → ISO UTC (« 2026-09-26T09:00:00.000Z »); "" if invalid. */
export function doualaToIso(local: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local.trim());
  if (!match) return "";
  const [, y, mo, d, h, mi] = match.map(Number);
  const asUtc = Date.UTC(y ?? 0, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  const offset = doualaParts(new Date(asUtc)) - asUtc;
  return new Date(asUtc - offset).toISOString();
}

/** ISO → « 2026-09-26T10:00 » for a datetime-local field, in Cameroon time. */
export function isoToDouala(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "";
  return new Date(doualaParts(value)).toISOString().slice(0, 16);
}

export interface LiveSessionView {
  registered: boolean;
  seats_left: number;
  join_url: string | null;
}

/** join: the room is open for this learner · soon: registered, room not open yet · register · full · locked (no plan). */
export type LiveAction = "join" | "soon" | "register" | "full" | "locked";

export function liveAction(session: LiveSessionView, hasAccess: boolean): LiveAction {
  if (session.registered) return session.join_url ? "join" : "soon";
  if (session.seats_left <= 0) return "full";
  return hasAccess ? "register" : "locked";
}

/** « 1440, 60 ; 10 » → [1440, 60, 10] (largest first); null when a value is not a whole number of minutes. */
export function parseReminderMinutes(text: string): number[] | null {
  const items = text
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!items.every((s) => /^\d+$/.test(s))) return null;
  const values = items.map(Number);
  if (values.some((v) => v < 1 || v > 10080)) return null;
  return [...new Set(values)].sort((a, b) => b - a);
}
